import { apiFetch, clearToken, getToken, setToken, ApiError } from '@/lib/api';
import {
  getDeviceFingerprint,
  getDeviceLabel,
  getTrustDevicePreference,
  setRememberedEmail,
  setTrustDevicePreference,
  clearRememberedEmail,
} from '@/lib/device';
import type { User } from '@/types/api';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

interface AuthState {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string, options?: { trustDevice?: boolean }) => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

interface RegisterData {
  name: string;
  email: string;
  phone: string;
  password: string;
  password_confirmation: string;
  legal_name: string;
  document: string;
  ui_locale?: string;
  country?: string;
  currency?: string;
  accept_terms?: boolean;
  ml_consent?: boolean;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!getToken()) {
      setUser(null);
      return;
    }
    try {
      const data = await apiFetch<{ user: User }>('/auth/me');
      setUser(data.user);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        clearToken();
        setUser(null);
      }
      throw err;
    }
  }, []);

  useEffect(() => {
    refresh()
      .catch(() => {
        /* Mantém token em falhas de rede; 401 já limpa em refresh(). */
      })
      .finally(() => setLoading(false));
  }, [refresh]);

  const login = useCallback(async (email: string, password: string, options?: { trustDevice?: boolean }) => {
    const trustDevice = options?.trustDevice ?? getTrustDevicePreference();
    const data = await apiFetch<{
      token?: string;
      user?: User;
      requires_otp?: boolean;
    }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({
        email,
        password,
        trust_device: trustDevice,
        device_fingerprint: getDeviceFingerprint(),
        device_label: getDeviceLabel(),
        platform: 'web',
      }),
    });

    if (data.requires_otp) {
      throw new Error('OTP necessário — desabilitado no protótipo. Contate o suporte.');
    }

    if (!data.token || !data.user) {
      throw new Error('Resposta de login inválida.');
    }

    setToken(data.token);
    setUser(data.user);
    setTrustDevicePreference(trustDevice);
    if (trustDevice) {
      setRememberedEmail(email);
    } else {
      clearRememberedEmail();
    }
  }, []);

  const register = useCallback(async (payload: RegisterData) => {
    const data = await apiFetch<{ token: string; user: User }>('/auth/register', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    setToken(data.token);
    setUser(data.user);
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiFetch('/auth/logout', { method: 'POST' });
    } finally {
      clearToken();
      setUser(null);
    }
  }, []);

  const value = useMemo(
    () => ({ user, loading, login, register, logout, refresh }),
    [user, loading, login, register, logout, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
